import styled from "styled-components";
import { FLEX } from "styles/flex";
import { Text } from "components/atoms/text/Text";
import * as mediaQuery from "components/MediaQuery";
import { MdFormatListBulleted } from "react-icons/md";

import { COLORS } from "styles/colors";
import { calcRem } from "styles/theme";
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { BsCheck } from "react-icons/bs";

interface TableFilter {
  label: string;
  value: string;
}
type TableColumnFilterTitleProps = {
  title: string;
  filters?: Array<TableFilter>;
  isFiltering?: string; // filters value string 으로 받아오기
  onClick?: (value: string | undefined) => void;
};

function TableColumnFilterTitle({
  title,
  filters,
  isFiltering,
  onClick,
}: TableColumnFilterTitleProps): JSX.Element {
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [isAll, setIsAll] = useState<boolean>(true);
  const [dropdownRect, setDropdownRect] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpenFilter) setDropdownRect(null);
  }, [isOpenFilter]);

  useLayoutEffect(() => {
    if (!isOpenFilter || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownRect({
      top: rect.bottom + 4,
      left: rect.left,
    });
  }, [isOpenFilter]);

  const closeFilter = () => setIsOpenFilter(false);
  const filterDropdownRef = useRef<HTMLUListElement>(null);
  const filterDropdown = isOpenFilter && dropdownRect && (
    <FilterWrapper
      style={{
        position: "fixed",
        top: dropdownRect.top,
        left: dropdownRect.left,
        zIndex: 10,
      }}
      ref={filterDropdownRef}
    >
      <li
        data-id="all"
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) {
            setIsAll(true);
            onClick("all");
            closeFilter();
          }
        }}
        data-testid="blocks-table-table-filter-item-all"
      >
        All
      </li>
      {filters?.map((filter, index) => (
        <li
          key={`table-filter-${index}`}
          data-id={filter.value}
          onClick={(e) => {
            e.stopPropagation();
            if (onClick) {
              setIsAll(false);
              onClick(filter.value);
              closeFilter();
            }
          }}
          data-testid={`blocks-table-table-filter-item-${filter.value}`}
        >
          {isFiltering && isFiltering === filter.value && <BsCheck />}
          {filter.label}
        </li>
      ))}
    </FilterWrapper>
  );

  useEffect(() => {
    if (!isOpenFilter) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (filterDropdownRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;

      closeFilter();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isOpenFilter]);

  return (
    <>
      <TitleContainer
        ref={triggerRef}
        isAll={isAll}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpenFilter((prev) => !prev);
        }}
        data-testid="blocks-table-table-title-filter-open"
      >
        <Text bold fontSize={14}>
          {title}
        </Text>
        <MdFormatListBulleted className="list-icon" />
        {/* <img src={isAll ? LIST_GRAY_ICON : LIST_ICON} alt="arrow down icon" /> */}
      </TitleContainer>
      {typeof document !== "undefined" && filterDropdown
        ? createPortal(filterDropdown, document.body)
        : null}
    </>
  );
}

const TitleContainer = styled.div<{ isAll: boolean }>`
  ${FLEX.FlexStartCenter};
  gap: 6px;
  cursor: pointer;
  position: relative;

  > img {
    width: 13px;
  }

  .list-icon {
    color: ${({ isAll, theme }) =>
      isAll ? theme.COLORS.WHITE : theme.COLORS.SELECTED};
  }

  ${mediaQuery.isMobile} {
    > img {
      width: 12px;
    }
  }
`;

const FilterWrapper = styled.ul`
  border-radius: 4px;
  background-color: ${COLORS.WHITE};
  border: 1px solid ${COLORS.LIGHTGRAY};
  padding: 4px 0;
  position: absolute;
  top: 50px;
  left: 0;
  z-index: 6;
  min-width: 80px;

  li {
    padding: 3px 6px;
    font-size: ${calcRem(12)};
    color: ${COLORS.BLACK};
    font-weight: normal;
    cursor: pointer;
    ${FLEX.FlexStartCenter};
    text-align: left;
    gap: 2px;

    &:hover {
      background-color: ${COLORS.LIGHTGRAY}20;
    }
  }
`;

export default TableColumnFilterTitle;
